// SAD-504 (SOL-08): reviewable AI suggestions beside the manual Solution editor.
// The manual save path is untouched; a suggestion changes nothing until a person applies it.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { projectDataClient } from '../../../lib/connect/clients';
const S = Testids.captureSolution;
const FIELDS = {
    approach: [
        'Approach',
        'approach'
    ],
    intended_outcomes: [
        'Intended outcomes',
        'intendedOutcomes'
    ],
    rationale: [
        'Rationale',
        'rationale'
    ],
    constraints: [
        'Constraints',
        'constraints'
    ],
    unresolved_issues: [
        'Unresolved issues',
        'unresolvedIssues'
    ]
};
const label = (target)=>FIELDS[target]?.[0] ?? target;
const currentText = (head, target)=>{
    const entry = FIELDS[target];
    return entry && head?.[entry[1]] || 'Not recorded';
};
export function SolutionSuggestions({ captureProjectId, head, dirtyRef }) {
    const queryClient = useQueryClient();
    const queryKey = [
        'capture-solution-suggestions',
        captureProjectId
    ];
    const [reviewing, setReviewing] = useState('');
    const [confirming, setConfirming] = useState('');
    const [reviewError, setReviewError] = useState('');
    const list = useQuery({
        queryKey,
        queryFn: async ()=>(await projectDataClient.listCaptureSolutionSuggestions({
                captureProjectId
            })).suggestions,
        enabled: !!head,
        retry: false
    });
    const generate = useMutation({
        mutationFn: ()=>projectDataClient.generateCaptureSolutionSuggestions({
                captureProjectId
            }),
        onSuccess: ()=>queryClient.invalidateQueries({
                queryKey
            })
    });
    const all = list.data ?? [];
    const pending = all.filter((s)=>s.state === 'pending');
    const reviewed = all.filter((s)=>s.state !== 'pending');
    async function review(suggestionId, decision) {
        setReviewing(suggestionId);
        setConfirming('');
        setReviewError('');
        try {
            await projectDataClient.reviewCaptureSolutionSuggestion({
                captureProjectId,
                suggestionId,
                decision
            });
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey
                }),
                decision === 'apply' ? queryClient.invalidateQueries({
                    queryKey: [
                        'capture-solution',
                        captureProjectId
                    ]
                }) : undefined
            ]);
        } catch (e) {
            setReviewError(ConnectError.from(e).rawMessage);
        } finally{
            setReviewing('');
        }
    }
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": S.aiSection,
        className: "space-y-3 rounded-md border p-3",
        children: [
            /*#__PURE__*/ _jsx("h3", {
                className: "font-semibold",
                children: "AI suggestions"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm",
                children: "Optional. Suggestions never change your Solution until you apply one; each applies to a single field and is saved as a new revision."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": S.aiSuggest,
                disabled: !head || generate.isPending,
                onClick: ()=>generate.mutate(),
                children: generate.isPending ? 'Suggesting…' : 'Suggest improvements'
            }),
            !head && /*#__PURE__*/ _jsx("p", {
                "data-testid": S.aiHint,
                className: "text-sm",
                children: "Save a revision first. Suggestions refine what you have written."
            }),
            generate.isError && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                "data-testid": S.aiError,
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: ConnectError.from(generate.error).rawMessage
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": S.aiRetry,
                        disabled: generate.isPending,
                        onClick: ()=>generate.mutate(),
                        children: "Try again"
                    })
                ]
            }),
            reviewError && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                "data-testid": S.aiReviewError,
                children: reviewError
            }),
            list.isError && /*#__PURE__*/ _jsxs("p", {
                role: "alert",
                children: [
                    "Could not load suggestions: ",
                    ConnectError.from(list.error).rawMessage
                ]
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-3",
                children: pending.map((s)=>/*#__PURE__*/ _jsxs("li", {
                        "data-testid": S.aiCard,
                        "data-suggestion-id": s.id,
                        "data-target-field": s.targetField,
                        className: "space-y-2 rounded-md border p-3",
                        children: [
                            /*#__PURE__*/ _jsx("h4", {
                                "data-testid": S.aiCardField,
                                className: "font-medium",
                                children: label(s.targetField)
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "grid gap-2 md:grid-cols-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "text-xs font-semibold",
                                                children: "Current"
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                "data-testid": S.aiCardCurrent,
                                                className: "whitespace-pre-wrap text-sm",
                                                children: currentText(head, s.targetField)
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "text-xs font-semibold",
                                                children: "Proposed"
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                "data-testid": S.aiCardProposed,
                                                className: "whitespace-pre-wrap text-sm",
                                                children: s.proposedText
                                            })
                                        ]
                                    })
                                ]
                            }),
                            s.rationale && /*#__PURE__*/ _jsxs("p", {
                                "data-testid": S.aiCardRationale,
                                className: "text-sm",
                                children: [
                                    "Why: ",
                                    s.rationale
                                ]
                            }),
                            confirming === s.id ? /*#__PURE__*/ _jsxs("div", {
                                role: "alert",
                                "data-testid": S.aiConfirm,
                                className: "space-y-2 text-sm",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        children: "The editor has unsaved edits. Applying reloads the working version and discards them. Save first to keep them."
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": S.aiConfirmApply,
                                        disabled: !!reviewing,
                                        onClick: ()=>review(s.id, 'apply'),
                                        children: "Discard edits and apply"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": S.aiConfirmCancel,
                                        onClick: ()=>setConfirming(''),
                                        children: "Cancel"
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": S.aiApply,
                                        disabled: reviewing === s.id,
                                        onClick: ()=>dirtyRef.current ? setConfirming(s.id) : review(s.id, 'apply'),
                                        children: "Apply"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": S.aiReject,
                                        disabled: reviewing === s.id,
                                        onClick: ()=>review(s.id, 'reject'),
                                        children: "Reject"
                                    })
                                ]
                            })
                        ]
                    }, s.id))
            }),
            reviewed.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1 text-sm",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "font-semibold",
                        children: [
                            "Reviewed (",
                            reviewed.length,
                            ")"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-1",
                        children: reviewed.map((s)=>/*#__PURE__*/ _jsxs("li", {
                                "data-testid": S.aiReviewed,
                                "data-state": s.state,
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                        children: s.state
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: label(s.targetField)
                                    })
                                ]
                            }, s.id))
                    })
                ]
            })
        ]
    });
}
